import React, { useState, useEffect } from 'react';
import { RefreshCw, PenLine, WifiOff, RotateCcw } from 'lucide-react';
import { Heading, MonoLabel, Body, Button } from '@/components/rc';
import type { ExtractionErrorCode } from '@/types';

interface ExtractionProgressProps {
  previewUrl: string | null;
  error: string | null;
  errorCode?: ExtractionErrorCode | null;
  isOffline?: boolean;
  onRetake: () => void;
  onManualEntry: () => void;
  onRetry?: () => void;
}

const SHIMMER_FIELDS = ['Producer', 'Wine Name', 'Vintage', 'Type', 'Region', 'Grape'];

function getErrorDisplay(errorCode?: ExtractionErrorCode | null, isOffline?: boolean) {
  if (isOffline) {
    return { heading: "YOU'RE OFFLINE", showRetry: false, showRetake: false };
  }
  switch (errorCode) {
    case 'PROXY_ERROR':
      return { heading: 'SOMETHING WENT WRONG', showRetry: true, showRetake: false };
    case 'TIMEOUT':
      return { heading: 'TAKING TOO LONG', showRetry: true, showRetake: false };
    case 'PARSE_ERROR':
      return { heading: "COULDN'T READ THE LABEL", showRetry: false, showRetake: true };
    default:
      return { heading: "COULDN'T READ THE LABEL", showRetry: false, showRetake: true };
  }
}

const ExtractionProgress: React.FC<ExtractionProgressProps> = ({
  previewUrl,
  error,
  errorCode,
  isOffline,
  onRetake,
  onManualEntry,
  onRetry,
}) => {
  const errorDisplay = error ? getErrorDisplay(errorCode, isOffline) : null;

  // Show encouraging message after 12s of waiting
  const [longWait, setLongWait] = useState(false);
  useEffect(() => {
    if (error) return;
    setLongWait(false);
    const timer = setTimeout(() => setLongWait(true), 12000);
    return () => clearTimeout(timer);
  }, [error]);

  return (
    <div className="flex flex-col items-center px-6 py-8 space-y-6 min-h-[60vh] sm:min-h-[50vh]">
      {/* Thumbnail */}
      {previewUrl && (
        <div className="w-40 h-40 rounded-[var(--rc-radius-md)] overflow-hidden border border-[var(--rc-border-emphasis)] bg-[var(--rc-surface-secondary)] flex-shrink-0">
          <img
            src={previewUrl}
            alt="Captured label"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {error && errorDisplay ? (
        /* Error State */
        <div className="text-center space-y-4 flex-1 flex flex-col items-center justify-center">
          {isOffline && <WifiOff size={32} className="text-[var(--rc-accent-coral)]" />}
          <Heading scale="heading">{errorDisplay.heading}</Heading>
          <Body size="body" colour="ghost" className="max-w-xs">
            {error}
          </Body>
          <div className="flex gap-3 pt-2">
            {errorDisplay.showRetry && onRetry && (
              <Button variantType="Secondary" label="RETRY" iconAsset={RotateCcw} iconPosition="Leading" onClick={onRetry} />
            )}
            {errorDisplay.showRetake && (
              <Button variantType="Secondary" label="RETAKE" iconAsset={RefreshCw} iconPosition="Leading" onClick={onRetake} />
            )}
            <Button variantType="Primary" label="ENTER MANUALLY" iconAsset={PenLine} iconPosition="Leading" onClick={onManualEntry} />
          </div>
        </div>
      ) : (
        /* Loading State */
        <div className="w-full max-w-sm space-y-5 flex-1">
          <div className="text-center space-y-1">
            <Heading scale="heading">{longWait ? 'TOUGH ONE' : 'READING LABEL'}</Heading>
            <MonoLabel size="micro" colour="ghost">
              {longWait ? 'Hmmm, this is a tough one. Hold on a little longer…' : 'Reviewing details…'}
            </MonoLabel>
          </div>

          {/* Progress bar */}
          <div className="w-full h-[2px] bg-[var(--rc-surface-secondary)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--rc-accent-acid)] rounded-full animate-progress-sweep"
              style={{
                animation: 'progress-sweep 2.5s ease-in-out infinite',
              }}
            />
          </div>

          {/* Shimmer field placeholders */}
          <div className="space-y-3">
            {SHIMMER_FIELDS.map((label, i) => (
              <div
                key={label}
                className="flex items-center gap-3"
                style={{ animationDelay: `${i * 150}ms` }}
              >
                <MonoLabel size="micro" colour="ghost" className="w-20 flex-shrink-0">
                  {label}
                </MonoLabel>
                <div
                  className="flex-1 h-4 rounded-[var(--rc-radius-sm)] bg-[var(--rc-surface-secondary)] animate-shimmer overflow-hidden relative"
                >
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-[var(--rc-surface-primary)] to-transparent"
                    style={{
                      animation: 'shimmer 1.5s ease-in-out infinite',
                      animationDelay: `${i * 150}ms`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inline keyframes via style tag */}
      <style>{`
        @keyframes progress-sweep {
          0% { width: 0%; margin-left: 0; }
          50% { width: 70%; margin-left: 15%; }
          100% { width: 0%; margin-left: 100%; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-shimmer, .animate-progress-sweep {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ExtractionProgress;
